import { Building2, Check, ChevronsUpDown, Loader2, PlusIcon, XIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/shadcn/button";
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/shadcn/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/shadcn/popover";
import { useAdaptiveDebounce } from "@/hooks/useAdaptiveDebounce";
import { useTranslations } from "@/hooks/useTranslations";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Badge } from "./shadcn/badge";

export interface CompanySuggestion {
    id: string;
    code: string;
    name: string;
    form?: string | null;
    reconciled?: boolean;
    address_label?: string | null;
}

export interface CompanyDetailsResponse {
    id: string;
    code: string;
    name: string;
    legal_form?: string | null;
    registration_date?: string | null;
    status?: string | null;
    vat?: string | null;
    reconciled?: boolean;
    address?: {
        label?: string | null;
        city?: string | null;
        municipality?: string | null;
        postal_code?: string | null;
        lat?: number | null;
        lon?: number | null;
        street?: string | null;
        house_number?: string | null;
    } | null;
}

export type CompanyRegistrySelection =
    | { mode: "registry"; suggestion: CompanySuggestion; details: CompanyDetailsResponse }
    | { mode: "manual"; name: string };

interface CompanyRegistryComboboxProps {
    value?: string;
    onSelectionChange?: (selection: CompanyRegistrySelection | null) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    className?: string;
    disabled?: boolean;
    allowClear?: boolean;
    clearText?: string;
    existingBadgeText?: string;
}

export function CompanyRegistryCombobox({
    value,
    onSelectionChange,
    placeholder = "Search registries…",
    searchPlaceholder = "Enter company name or code",
    emptyText = "Search for a company",
    className,
    disabled = false,
    allowClear = true,
    clearText = "Clear selection",
    existingBadgeText = "Existing",
}: CompanyRegistryComboboxProps) {
    const { t } = useTranslations();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<CompanySuggestion[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isFetchingDetails, setIsFetchingDetails] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [selectedLabel, setSelectedLabel] = React.useState(value ?? "");
    const [selectedCode, setSelectedCode] = React.useState<string | null>(null);
    const commandListRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setSelectedLabel(value ?? "");
        if (!value) {
            setSelectedCode(null);
        }
    }, [value]);

    const debouncedSearchTerm = useAdaptiveDebounce(searchTerm, {
        baseDelay: 400,
        fastTypingDelay: 650,
        typingThreshold: 150,
    });

    React.useEffect(() => {
        if (debouncedSearchTerm.trim().length < 2) {
            setOptions([]);
            return;
        }

        void searchCompanies(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    const searchCompanies = async (query: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(`/api/company/suggest?q=${encodeURIComponent(query)}`, {
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            if (!response.ok) {
                throw new Error(`Company suggest failed with ${response.status}`);
            }

            const data = await response.json();
            const suggestions = Array.isArray(data) ? (data as CompanySuggestion[]) : [];
            setOptions(suggestions);
        } catch (error) {
            console.error("Company search error:", error);
            toast.error(t('toasts.ui_company_registry_combobox.search_failed', 'Nepavyko rasti įmonių. Bandykite dar kartą.'));
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCompanyDetails = async (code: string) => {
        try {
            const response = await fetch(`/api/company/${encodeURIComponent(code)}`, {
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            if (!response.ok) {
                throw new Error(`Company details failed with ${response.status}`);
            }

            return (await response.json()) as CompanyDetailsResponse;
        } catch (error) {
            console.error("Company details error:", error);
            toast.error(t('toasts.ui_company_registry_combobox.details_failed', 'Nepavyko gauti įmonės duomenų.'));
            return null;
        }
    };

    const applyManualSelection = (label: string) => {
        const trimmed = label.trim();
        if (!trimmed) {
            return;
        }

        setSelectedLabel(trimmed);
        setSelectedCode(null);
        onSelectionChange?.({ mode: "manual", name: trimmed });
        setSearchTerm("");
        setOpen(false);
    };

    const handleSuggestionSelect = async (suggestion: CompanySuggestion) => {
        setIsFetchingDetails(true);
        try {
            const details = await fetchCompanyDetails(suggestion.code);
            if (!details) {
                return;
            }

            const display = `${suggestion.name} — ${suggestion.code}`;
            setSelectedLabel(display);
            setSelectedCode(suggestion.code);
            onSelectionChange?.({ mode: "registry", suggestion, details });
            setSearchTerm("");
            setOpen(false);
        } finally {
            setIsFetchingDetails(false);
        }
    };

    const handleClearSelection = () => {
        setSelectedLabel("");
        setSelectedCode(null);
        onSelectionChange?.(null);
        setSearchTerm("");
        setOptions([]);
        setOpen(false);
    };

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
        if (value.length === 0) {
            setOptions([]);
        }
    };

    const handleKeyDown = async (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" || !searchTerm.trim()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const activeItem = commandListRef.current?.querySelector('[cmdk-item][aria-selected="true"]') as
            | HTMLElement
            | null;
        const selectedValue = activeItem?.dataset.value;

        if (selectedValue && options.length > 0) {
            const suggestion = options.find((option) => option.code === selectedValue);
            if (suggestion) {
                await handleSuggestionSelect(suggestion);
                return;
            }
        }

        if (options.length === 0) {
            applyManualSelection(searchTerm);
        }
    };

    return (
        <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn("w-full justify-between", className)}
                    disabled={disabled}
                >
                    <span className="truncate flex items-center gap-2">
                        {isFetchingDetails ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                <span>Įkeliame įmonę…</span>
                            </>
                        ) : selectedLabel ? (
                            <>
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="truncate">{selectedLabel}</span>
                            </>
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0" align="start">
                <Command shouldFilter={false} className="w-full">
                    {/* Registry lookups return nothing until a query is typed,
                        so focusing the field immediately is the right default. */}
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onValueChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    <CommandList ref={commandListRef}>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Ieškome įmonių...
                            </div>
                        ) : (
                            <>
                                <CommandGroup>
                                    {options.length > 0 ? (
                                        options.map((option) => (
                                            <CommandItem
                                                key={option.code}
                                                value={option.code}
                                                data-value={option.code}
                                                keywords={[
                                                    option.name,
                                                    option.code,
                                                    option.address_label ?? "",
                                                    option.form ?? "",
                                                ]}
                                                onSelect={() => void handleSuggestionSelect(option)}
                                                className="hover:bg-accent cursor-pointer"
                                            >
                                                <Check
                                                    className={cn(
                                                        "h-4 w-4",
                                                        selectedCode === option.code ? "opacity-100" : "opacity-0",
                                                    )}
                                                />

                                                <div className="flex items-center gap-2 justify-between w-full">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-muted-foreground">
                                                            <Building2 className="h-4 w-4" />
                                                        </span>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="font-medium">{option.name}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <Badge variant="outline">{option.code}</Badge>
                                                        {option.reconciled ? (
                                                            <Badge variant="secondary">{existingBadgeText}</Badge>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))
                                    ) : (
                                        <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
                                    )}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
                <div className="border-t p-1">
                    <div className="flex flex-col gap-1">
                        {searchTerm.trim() && (
                            <Button
                                variant="ghost"
                                className="w-full justify-start font-normal"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    applyManualSelection(searchTerm);
                                }}
                                type="button"
                            >
                                <PlusIcon size={16} className="-ms-2 opacity-60" aria-hidden="true" />
                                <span className="ml-1 font-medium">{searchTerm}</span>
                            </Button>
                        )}
                        {allowClear && (
                            <Button
                                variant="ghost"
                                className="w-full justify-start font-normal"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleClearSelection();
                                }}
                                type="button"
                            >
                                <XIcon size={16} className="-ms-2 opacity-60" aria-hidden="true" />
                                {clearText}
                            </Button>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
